export const DEFAULT_API_BASE = import.meta.env.PROD ? "https://api.beamo.tech/api/v1" : "/api/v1";

export function normalizeApiBase(value?: string | null): string {
  const trimmed = value?.trim() || "";
  if (!trimmed) return DEFAULT_API_BASE;

  const normalized = trimmed.replace(/\/+$/, "");
  if (!normalized) return DEFAULT_API_BASE;
  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (normalized.startsWith("/")) return normalized;
  return `/${normalized}`;
}

export const API_BASE = normalizeApiBase(import.meta.env.VITE_API_BASE);

type ApiErrorOptions = {
  code?: string;
  detail?: unknown;
  payload?: unknown;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;
  payload?: unknown;

  constructor(status: number, message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = options.code;
    this.detail = options.detail;
    this.payload = options.payload;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isJsonContentType(contentType: string): boolean {
  return contentType.toLowerCase().includes("json");
}

function formatValidationLocation(loc: unknown): string | null {
  if (!Array.isArray(loc)) return null;
  const parts = loc
    .filter((part): part is string | number => typeof part === "string" || typeof part === "number")
    .map(String)
    .filter((part) => part !== "body");
  return parts.length ? parts.join(".") : null;
}

function formatValidationErrors(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const messages = detail
    .map((item) => {
      if (typeof item === "string") {
        const message = item.trim();
        return message || null;
      }
      if (!isRecord(item) || typeof item.msg !== "string") return null;
      const location = formatValidationLocation(item.loc);
      return location ? `${location}: ${item.msg}` : item.msg;
    })
    .filter((message): message is string => Boolean(message));
  return messages.length ? messages.join("; ") : null;
}

function parseApiErrorPayload(status: number, payload: unknown): ApiError {
  const fallbackMessage = `Request failed: ${status}`;
  const detail = isRecord(payload) ? payload.detail : undefined;
  const detailMessage =
    typeof detail === "string"
      ? detail.trim() || null
      : isRecord(detail) && typeof detail.message === "string"
        ? detail.message.trim() || null
        : null;
  const topLevelMessage =
    isRecord(payload) && typeof payload.message === "string" ? payload.message.trim() || null : null;
  const validationMessage = formatValidationErrors(detail);
  const code =
    isRecord(detail) && typeof detail.code === "string" && detail.code.trim() ? detail.code.trim() : undefined;

  return new ApiError(status, detailMessage || topLevelMessage || validationMessage || fallbackMessage, {
    code,
    detail,
    payload,
  });
}

async function buildApiError(response: Response): Promise<ApiError> {
  const fallbackMessage = `Request failed: ${response.status}`;
  const contentType = response.headers.get("content-type") || "";
  if (isJsonContentType(contentType)) {
    try {
      return parseApiErrorPayload(response.status, await response.json());
    } catch {
      return new ApiError(response.status, fallbackMessage);
    }
  }

  const text = (await response.text()).trim();
  return new ApiError(response.status, text || fallbackMessage);
}

function getCookie(name: string): string | null {
  const target = `${name}=`;
  const parts = document.cookie.split(";").map((item) => item.trim());
  for (const part of parts) {
    if (part.startsWith(target)) {
      return decodeURIComponent(part.substring(target.length));
    }
  }
  return null;
}

export function buildApiUrl(path: string, apiBase = API_BASE): string {
  const normalizedBase = normalizeApiBase(apiBase);
  const normalizedPath = path.trim().replace(/^\/+/, "");
  if (!normalizedPath) return normalizedBase;
  return `${normalizedBase}/${normalizedPath}`;
}

export async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method || "GET").toUpperCase();
  const isSafeMethod = ["GET", "HEAD", "OPTIONS"].includes(method);
  // Normalize through Headers so object literals, tuple arrays, and real Headers instances all survive intact.
  const headers = new Headers(init?.headers);
  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;
  if (!isSafeMethod && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!isSafeMethod) {
    const csrf = getCookie("csrf_token");
    if (csrf && !headers.has("X-CSRF-Token")) {
      headers.set("X-CSRF-Token", csrf);
    }
  }

  const response = await fetch(buildApiUrl(path), {
    credentials: "include",
    ...init,
    headers,
  });

  if (!response.ok) {
    throw await buildApiError(response);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
}
